import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { format, parse, isValid } from "date-fns";
import CommonSelect from "../../common/CommonSelect";
import { validateGeneralContactField } from "./scriptcogeneralcontactdetails.helper";
import ScriptCoPatientDocumentUpload from "./ScriptCoPatientDocumentUpload";

const convertToMmDdYyyy = (dateStr) => {
  if (!dateStr) return "";
  const trimmed = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split("-");
    return `${m}/${d}/${y}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    return trimmed;
  }
  return dateStr;
};

const ScriptCoGeneralAndContactDetails = ({
  opportunity,
  data = {},
  onDataChange,
  onValidationChange,
  showErrors,
  patientDocument,
  onPatientDocumentChange,
  onPatientDocValidationChange,
}) => {
  const member = opportunity?.member || {};
  const scriptCoRaw =
    opportunity?.scriptCoDetails || opportunity?.grmDetails || {};
  const scriptCoDetails =
    scriptCoRaw?.data?.[0] ||
    (Array.isArray(scriptCoRaw?.data)
      ? scriptCoRaw.data[0]
      : scriptCoRaw?.data) ||
    scriptCoRaw?.result?.[0] ||
    scriptCoRaw ||
    {};
  const customerDetails =
    scriptCoDetails?.customerDetails || scriptCoDetails || {};

  const getField = (keys) => {
    for (const key of keys) {
      if (member[key]) return member[key];
      if (opportunity?.[key]) return opportunity[key];
    }
    return "";
  };

  const fName = getField(["firstName", "FirstName", "first_name"]);
  const lName = getField(["lastName", "LastName", "last_name"]);
  const fullName = getField(["fullName", "memberName", "name"]);
  const splitName = (fullName || "").split(" ");

  const firstName = fName || (splitName.length > 0 ? splitName[0] : "");
  const lastName =
    lName || (splitName.length > 1 ? splitName.slice(1).join(" ") : "");

  const initialMobile =
    data.mobileNumber ||
    customerDetails.phone ||
    customerDetails.mobilePhone ||
    member.mobilePhone ||
    member.phoneNumber ||
    member.phone ||
    "";
  const initialEmail =
    data.email ||
    customerDetails.email ||
    member.email ||
    member.contact?.email ||
    "";

  const rawDob =
    data.dob ||
    customerDetails.dob ||
    customerDetails.dateOfBirth ||
    customerDetails.Dob ||
    member.dateOfBirth ||
    member.dob ||
    "";
  const initialDob = convertToMmDdYyyy(rawDob);

  const rawGender =
    data.gender ||
    customerDetails.gender ||
    customerDetails.sex ||
    customerDetails.Gender ||
    member.sex ||
    member.gender ||
    "";

  const getGenderInitial = (rawGender) => {
    if (!rawGender) return "";
    const lower = rawGender.toLowerCase();
    if (lower.startsWith("m")) return "Male";
    if (lower.startsWith("f")) return "Female";
    return rawGender;
  };

  const initialGender = getGenderInitial(rawGender);

  const initialCountryCode =
    data.countryCode ||
    customerDetails.countryCode ||
    customerDetails.CountryCode ||
    member.countryCode ||
    "US";

  const [formData, setFormData] = useState(() => ({
    ...data,
    mobileNumber: initialMobile,
    email: initialEmail,
    dob: initialDob,
    gender: initialGender,
    countryCode: initialCountryCode,
  }));

  useEffect(() => {
    if (onDataChange) {
      onDataChange(formData);
    }
  }, [formData, onDataChange]);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [anchorEls, setAnchorEls] = useState({});

  const anchorRefs = {
    gender: useRef(null),
  };

  useEffect(() => {
    const fields = ["mobileNumber", "email", "dob", "gender", "countryCode"];
    const initialErrors = {};
    fields.forEach((field) => {
      const error = validateGeneralContactField(
        field,
        formData[field],
        formData,
      );
      if (error) {
        initialErrors[field] = error;
      }
    });
    setErrors(initialErrors);
  }, [formData]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(Object.keys(errors).length === 0);
    }
  }, [errors, onValidationChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobileNumber") {
      const sanitized = value.replaceAll(/\D/g, "").slice(0, 10);
      setFormData((prev) => {
        const next = { ...prev, [name]: sanitized };
        onDataChange(next);
        return next;
      });
    } else {
      setFormData((prev) => {
        const next = { ...prev, [name]: value };
        onDataChange(next);
        return next;
      });
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const getErrorProps = (name) => {
    const isInvalid = !!errors[name];
    const shouldShow = touched[name] || showErrors;
    return {
      error: isInvalid && shouldShow,
      helperText: shouldShow ? errors[name] : "",
    };
  };

  const handleOpenSelect = (field) => (event) => {
    setAnchorEls((prev) => ({ ...prev, [field]: event.currentTarget }));
  };

  const handleCloseSelect = (field) => () => {
    setAnchorEls((prev) => ({ ...prev, [field]: null }));
  };

  const handleSelection = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      onDataChange(next);
      return next;
    });
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const getWidth = (field) => {
    return anchorRefs[field]?.current?.offsetWidth
      ? `${anchorRefs[field].current.offsetWidth}px`
      : "240px";
  };

  const renderSelectTrigger = (field, value, placeholder) => {
    const isOpen = Boolean(anchorEls[field]);
    return (
      <>
        <Box
          ref={anchorRefs[field]}
          onClick={handleOpenSelect(field)}
          sx={{
            width: "100%",
            height: "45px",
            border: "1px solid",
            borderColor: getErrorProps(field).error ? "#d32f2f" : "#E8E8E9",
            borderRadius: "8px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 12px",
            backgroundColor: "#FFFFFF",
            cursor: "pointer",
            "&:hover": { backgroundColor: "#F9FAFB" },
          }}
        >
          <Typography
            sx={{ fontSize: "14px", color: value ? "#0e1324" : "#999893" }}
          >
            {value || placeholder}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              color: getErrorProps(field).error ? "#d32f2f" : "#6B7280",
              fontSize: "20px",
              transition: "transform 0.2s",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </Box>
        {getErrorProps(field).error && (
          <Typography
            sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 0, ml: 1.75 }}
          >
            {getErrorProps(field).helperText}
          </Typography>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        p: 2,
        border: "1px solid #E8E8E9",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}>
        General and Contact Details
      </Typography>

      {/* Row 1: Patient Document Upload */}
      {onPatientDocumentChange && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <ScriptCoPatientDocumentUpload
            data={patientDocument}
            onChange={onPatientDocumentChange}
            onValidationChange={onPatientDocValidationChange}
            showErrors={showErrors}
          />
        </Box>
      )}

      {/* Row 2: First Name & Last Name */}
      <Box sx={{ display: "flex", gap: 3 }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            First Name<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            value={firstName}
            disabled
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F2F2F2",
                borderRadius: "8px",
                "& fieldset": { border: "none" },
              },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#999893",
                fontSize: "14px",
                py: 1.5,
              },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            Last Name<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            value={lastName}
            disabled
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F2F2F2",
                borderRadius: "8px",
                "& fieldset": { border: "none" },
              },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#999893",
                fontSize: "14px",
                py: 1.5,
              },
            }}
          />
        </Box>
      </Box>

      {/* Row 2: Mobile Number & Email */}
      <Box sx={{ display: "flex", gap: 3 }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            Mobile Number<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            name="mobileNumber"
            variant="outlined"
            size="small"
            value={formData.mobileNumber || ""}
            onChange={handleChange}
            onBlur={() => handleBlur("mobileNumber")}
            placeholder="Enter mobile number"
            {...getErrorProps("mobileNumber")}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{ fontSize: "12px", color: "#666", mr: 0.5 }}
                    >
                      {(formData.mobileNumber || "").length}/10
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: getErrorProps("mobileNumber").error
                    ? "#d32f2f"
                    : "#E8E8E9",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
              },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            Email<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            name="email"
            variant="outlined"
            size="small"
            value={formData.email || ""}
            placeholder="Enter email address"
            onChange={handleChange}
            onBlur={() => handleBlur("email")}
            {...getErrorProps("email")}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: getErrorProps("email").error
                    ? "#d32f2f"
                    : "#E8E8E9",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
              },
            }}
          />
        </Box>
      </Box>

      {/* Row 3: Dob, Gender, Country Code */}
      <Box sx={{ display: "flex", gap: 3 }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            Date of Birth<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <DesktopDatePicker
            value={
              formData.dob
                ? parse(formData.dob, "MM/dd/yyyy", new Date())
                : null
            }
            onChange={(newValue) => {
              const formatted =
                newValue && isValid(newValue)
                  ? format(newValue, "MM/dd/yyyy")
                  : "";
              setFormData((prev) => {
                const next = { ...prev, dob: formatted };
                onDataChange(next);
                return next;
              });
            }}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                placeholder: "MM/DD/YYYY",
                ...getErrorProps("dob"),
                sx: {
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#FFFFFF",
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: getErrorProps("dob").error
                        ? "#d32f2f"
                        : "#E8E8E9",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "14px",
                    py: 1.5,
                    color: "#0e1324",
                  },
                },
              },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            Sex<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          {renderSelectTrigger("gender", formData.gender, "Select")}
          <CommonSelect
            id="gender-select"
            open={Boolean(anchorEls.gender)}
            anchorEl={anchorEls.gender}
            onClose={handleCloseSelect("gender")}
            noteTypes={["Male", "Female"]}
            selectedTypes={formData.gender ? [formData.gender] : []}
            handleTypeToggle={(type) => {
              handleSelection("gender", type);
              handleCloseSelect("gender")();
            }}
            width={getWidth("gender")}
          />
        </Box>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            Country Code<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            name="countryCode"
            variant="outlined"
            size="small"
            value={formData.countryCode || "US"}
            onChange={handleChange}
            onBlur={() => handleBlur("countryCode")}
            placeholder="e.g. US"
            {...getErrorProps("countryCode")}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: getErrorProps("countryCode").error
                    ? "#d32f2f"
                    : "#E8E8E9",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

ScriptCoGeneralAndContactDetails.propTypes = {
  opportunity: PropTypes.object,
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
  patientDocument: PropTypes.object,
  onPatientDocumentChange: PropTypes.func,
  onPatientDocValidationChange: PropTypes.func,
};

export default ScriptCoGeneralAndContactDetails;
